import type { BeckyModel } from '@wix/thunderbolt-becky-root'
import type { Data, FetchFn, MetricsReporter, RefSchemas } from '@wix/thunderbolt-becky-types'
import { shapeIdsNode as shapeIds } from './shapeIdsNode'
import { cursorShapeIdsNode as cursorShapeIds } from './cursorShapeIdsNode'
import { BuildSvgUrl, fetchShapes } from './fetchShapes'
import { getInvalidationHandler } from './invalidationHandler'
import { createSimpleMaterializer } from '@wix/materializer'
import { Container } from '@wix/thunderbolt-ioc'
import {
	CatharsisConstants,
	CatharsisSingleTransactionSymbol,
	CompNodeProviderSymbol,
	MaterializerFactorySymbol,
	OnInvalidationSymbol,
	css as catharsisIocLoader,
} from '@wix/thunderbolt-catharsis-ioc'

const RELEVANT_DATA_MAPS: Array<keyof Data> = [
	'document_data',
	'design_data',
	'component_properties',
	'theme_data',
	'cursor',
]

export const findShapes = (
	beckyModel: BeckyModel,
	schemas: RefSchemas,
	runAndReport: <T>(fn: () => T, name: string) => T = (fn) => fn()
): Array<string> => {
	const set = new Set<string>()

	const container = Container()
	const catharsisConstants: CatharsisConstants = {
		componentsMappers: beckyModel.componentsMappers,
		experiments: beckyModel.experiments,
		isEditor: false,
		isExperimentOpen: (exp) => !!beckyModel.experiments[exp],
		schemas,
		isResponsiveMasterPage: beckyModel.isResponsive && beckyModel.pageCompId === 'masterPage',
	}

	container.load((bind) => catharsisIocLoader(bind, catharsisConstants))
	const onInvalidation = getInvalidationHandler(set)
	container.bind(OnInvalidationSymbol).toConstantValue(onInvalidation)

	container.bind(MaterializerFactorySymbol).toConstantValue(createSimpleMaterializer)

	container.bind(CompNodeProviderSymbol).toConstantValue({
		getCompNodes: () => ({ shapeIds, cursorShapeIds }),
		getUsedDataMaps: () => RELEVANT_DATA_MAPS,
	})

	const singleTransaction = container.get(CatharsisSingleTransactionSymbol)

	singleTransaction.execute(beckyModel, runAndReport)

	return [...set]
}

export const fetchComponentShapes = (
	beckyModel: BeckyModel,
	fetchFn: FetchFn,
	mediaRootUrl: string,
	buildSvgUrl: BuildSvgUrl,
	metricsReporter: MetricsReporter,
	schemas: RefSchemas
): Promise<Record<string, string>> => {
	const shapes = metricsReporter.runAndReport(
		() => findShapes(beckyModel, schemas, metricsReporter.runAndReport),
		'tb-css-find-shapes'
	)
	return fetchShapes(shapes, fetchFn, mediaRootUrl, buildSvgUrl, metricsReporter)
}
